/**
 * The count-and-filter tiles above an admin CRM table.
 *
 * One definition for every table, because these started on the Payments view and
 * were then re-typed for Members — same 108px tile, same 44px icon, same
 * selected treatment — and the two immediately began to drift in padding and
 * radius. A fifth table would have produced a fifth copy.
 *
 * The idea the shape encodes: a number on screen is always the control that
 * filters to it. Counts that merely report, sitting beside separate pills that
 * filter, make an administrator read the same figure twice and leave a selection
 * visible in only one of the two places.
 *
 * Each tile owns its own selected state and handler, so a table can map several
 * axes onto one row — a population on some tiles, a narrowing on others — rather
 * than being forced into one mutually exclusive group.
 */
import type { LucideIcon } from "lucide-react";

export type CrmFilterTile = {
  /** Stable identity, used as the React key. */
  key: string;
  label: string;
  /** How many rows this tile matches. */
  value: number | string;
  icon: LucideIcon;
  /** Accent for the icon and the selected border. */
  color: string;
  /** Fill when selected. */
  background: string;
  /** Border when not selected. */
  border: string;
  selected: boolean;
  onSelect: () => void;
  /** Native tooltip, for a tile whose meaning is not obvious from its label. */
  title?: string;
};

export default function CrmFilterTiles({
  tiles,
  ariaLabel,
}: {
  tiles: CrmFilterTile[];
  ariaLabel: string;
}) {
  return (
    /*
     * Column floor of 228px: the icon takes 44px plus its gap off the front of
     * every tile, and below this the label wraps under the count.
     */
    <section
      aria-label={ariaLabel}
      style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(228px,1fr))", gap: ".8rem", marginBottom: "1.25rem" }}
    >
      {tiles.map(tile => {
        const Icon = tile.icon;
        return (
          /*
            Icon left, label and count to its right, rather than the icon stacked
            above them. At this size the icon is the tile's landmark — it is what
            distinguishes "cancelled" from "past due" at a glance — so it holds
            the left edge and the text reads as one block beside it.
          */
          <button
            key={tile.key}
            type="button"
            onClick={tile.onSelect}
            aria-pressed={tile.selected}
            title={tile.title}
            style={{
              minHeight: 108, display: "flex", alignItems: "center", gap: "1rem", textAlign: "left",
              padding: "1.05rem", borderRadius: ".9rem",
              border: `1px solid ${tile.selected ? tile.color : tile.border}`,
              background: tile.selected ? tile.background : "rgba(255,255,255,.025)",
              color: "white", cursor: "pointer",
              boxShadow: tile.selected ? `0 0 0 1px ${tile.color}22, 0 10px 30px rgba(0,0,0,.16)` : "none",
              transition: "border-color 160ms ease, background 160ms ease",
            }}
          >
            {/* Never shrinks: the grid is auto-fit, and a squashed landmark is worse than a wrapped label. */}
            <Icon size={44} strokeWidth={1.5} style={{ color: tile.color, flex: "0 0 auto" }} aria-hidden="true" />
            <span style={{ minWidth: 0 }}>
              <span style={{ display: "block", color: "#64748B", fontFamily: "'Space Grotesk', sans-serif", fontWeight: 700, fontSize: "var(--chi-body-compact)", letterSpacing: ".06em", textTransform: "uppercase" }}>
                {tile.label}
              </span>
              <strong style={{ display: "block", marginTop: ".14rem", fontFamily: "'Space Grotesk', sans-serif", color: "white", fontSize: "1.85rem", lineHeight: 1.1 }}>
                {tile.value}
              </strong>
            </span>
          </button>
        );
      })}
    </section>
  );
}
